import { useEffect, useRef } from 'react';
import { getSupabase } from '@/lib/supabase';
import type { Lang } from '@/types/lang';

/**
 * useRoleTranslationRefresh — Family (Oturum 9, 00:10)
 *
 * Detects when a family member's `role_label_translations[viewerLang]` is
 * empty, then fires a background `refresh-role-translations` Edge Function
 * request for that member. Within ~5 seconds the translation lands in DB,
 * and the next TanStack Query refetch (default refetchInterval: 8000) shows
 * the localized role label.
 *
 * Session-local dedup: a Set tracks userIds we've already triggered this
 * session, so we don't spam the endpoint if Anthropic chatter detection keeps
 * rejecting (frontend will keep seeing empty translation, but we don't fire
 * repeat requests).
 *
 * Use in: ContactList.tsx, Home.tsx — anywhere we render a list of family
 * members with role labels.
 */
interface MemberWithRoleTranslations {
  id: string;
  role_label: string;
  role_label_translations?: Record<string, string> | null;
}

export function useRoleTranslationRefresh(
  members: MemberWithRoleTranslations[] | undefined,
  viewerLang: Lang,
): void {
  const triggeredRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!members || members.length === 0) return;
    if (typeof window === 'undefined') return;

    const supabase = getSupabase();
    for (const member of members) {
      const translation = member.role_label_translations?.[viewerLang];
      const needsRefresh =
        !translation || translation.trim().length === 0;
      if (!needsRefresh) continue;
      if (triggeredRef.current.has(member.id)) continue;

      triggeredRef.current.add(member.id);

      // Fire & forget — TanStack Query will refetch profile data on its own
      // schedule and reveal the new translation. Errors logged but not fatal.
      void supabase.functions
        .invoke('refresh-role-translations', {
          body: { targetUserId: member.id },
        })
        .catch((err: unknown) => {
          console.warn('role-translation-refresh fail', member.id, err);
        });
    }
  }, [members, viewerLang]);
}
